import React, { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { CopyIcon, SearchIcon, ArrowRightLeftIcon } from "lucide-react";
import PaginatedTable, { PaginatedTableColumn } from "../../ui/paginated-table";
import moment from "moment";

interface ReviewItem {
  request_id: string;
  date_created: number;
  params: any;
  model: string;
  original_response: string;
  result: string | null; // "GOOD", "CORRECTED", "ERROR"
  result_detail: string | null; // Correction text if corrected
  provider: string | null;
  review_date_completed: number | null;
}

interface PreviewHoverCardProps {
  originalResponse: string;
  correctedResponse?: string | null;
  isCorrected: boolean;
}

const PreviewHoverCard: React.FC<PreviewHoverCardProps> = ({
  originalResponse,
  correctedResponse,
  isCorrected
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isHovered && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Wider card for side-by-side layout when corrected
      const cardWidth = isCorrected && correctedResponse ? 700 : 400;
      const cardHeight = 200;
      
      // Position to the left of the button, vertically centered
      let left = rect.left - cardWidth - 8;
      let top = rect.top + rect.height / 2 - cardHeight / 2;
      
      // If would go off left edge, position to the right instead
      if (left < 8) {
        left = rect.right + 8;
      }
      
      // If still goes off right edge, center it
      if (left + cardWidth > window.innerWidth - 8) {
        left = Math.max(8, (window.innerWidth - cardWidth) / 2);
      }
      
      // Keep within viewport vertically
      if (top < 8) {
        top = 8;
      } else if (top + cardHeight > window.innerHeight - 8) {
        top = window.innerHeight - cardHeight - 8;
      }
      
      setPosition({ top, left });
    }
  }, [isHovered, isCorrected, correctedResponse]);

  return (
    <>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SearchIcon className="w-4 h-4" />
      </Button>
      
      {isHovered && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ top: position.top, left: position.left }}
        >
          <div 
            className={`bg-popover border border-border rounded-lg shadow-xl p-4 text-sm animate-in fade-in-0 zoom-in-95 duration-150 ${
              isCorrected && correctedResponse ? "w-[700px]" : "w-[400px]"
            }`}
          >
            {isCorrected && correctedResponse ? (
              // Side-by-side layout for corrected items
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-1.5">
                    Original Response
                  </div>
                  <div className="bg-muted/50 rounded-md p-3 h-40 overflow-y-auto text-foreground whitespace-pre-wrap text-xs leading-relaxed pointer-events-auto">
                    {originalResponse || "No response"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-gc-med text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1.5">
                    Corrected Response
                  </div>
                  <div className="bg-blue-50/50 dark:bg-blue-950/30 rounded-md p-3 h-40 overflow-y-auto text-foreground whitespace-pre-wrap text-xs leading-relaxed border border-blue-200/50 dark:border-blue-800/30 pointer-events-auto">
                    {correctedResponse}
                  </div>
                </div>
              </div>
            ) : (
              // Single column for non-corrected items
              <div>
                <div className="text-xs font-gc-med text-muted-foreground uppercase tracking-wide mb-1.5">
                  Model Response
                </div>
                <div className="bg-muted/50 rounded-md p-3 max-h-40 overflow-y-auto text-foreground whitespace-pre-wrap text-xs leading-relaxed pointer-events-auto">
                  {originalResponse || "No response"}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

interface ReviewItemsTableProps {
  items: ReviewItem[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onViewDiff: (item: ReviewItem) => void;
  apiKey: ApiKeyOrGetter;
  emptyTitle?: string;
  emptyDescription?: string;
}

const ReviewItemsTable: React.FC<ReviewItemsTableProps> = ({
  items,
  totalItems,
  currentPage,
  pageSize,
  loading,
  onPageChange,
  onViewDiff,
  apiKey,
  emptyTitle = "No review items found",
  emptyDescription = "No requests found for this review."
}) => {
  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getResultBadge = (result: string | null) => {
    if (!result || result === "PENDING") {
      return (
        <Badge variant="outline" className="border-gray-500/30 text-gray-500 bg-gray-50 dark:bg-gray-950/20 dark:text-gray-400">
          PENDING
        </Badge>
      );
    }

    switch (result.toUpperCase()) {
      case "GOOD":
      case "PASS":
        return (
          <Badge variant="outline" className="border-green-500/30 text-green-700 bg-green-50 dark:bg-green-950/20 dark:text-green-400">
            PASS
          </Badge>
        );
      case "CORRECTED":
        return (
          <Badge variant="outline" className="border-blue-500/30 text-blue-700 bg-blue-50 dark:bg-blue-950/20 dark:text-blue-400">
            CORRECTED
          </Badge>
        );
      case "ERROR":
      case "BAD":
      case "FAILED":
        return (
          <Badge variant="outline" className="border-red-500/30 text-red-700 bg-red-50 dark:bg-red-950/20 dark:text-red-400">
            ERROR
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-gray-500">
            {result.toUpperCase()}
          </Badge>
        );
    }
  };

  const columns: PaginatedTableColumn<ReviewItem>[] = useMemo(() => [
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[15%]",
      render: (item) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(item.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(item.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "request_id",
      header: "Request ID",
      width: "w-[25%]",
      render: (item) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
            {item.request_id && item.request_id.length > 12
              ? `${item.request_id.substring(0, 6)}...${item.request_id.substring(
                  item.request_id.length - 6
                )}`
              : item.request_id}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(item.request_id || "");
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
        </div>
      )
    },
    {
      key: "model",
      header: "Model",
      width: "w-[20%]",
      render: (item) => (
        <span className="text-sm text-foreground">
          {item.model || "—"}
        </span>
      )
    },
    {
      key: "result",
      header: "Result",
      width: "w-[15%]",
      render: (item) => getResultBadge(item.result)
    },
    {
      key: "result_detail", 
      header: "Actions",
      width: "w-[25%]",
      render: (item) => {
        const isCorrected = item.result === "CORRECTED";
        const hasResponse = item.original_response || item.result_detail;
        
        return (
          <div className="flex items-center gap-2">
            {/* Preview hover card */}
            {hasResponse && (
              <PreviewHoverCard
                originalResponse={item.original_response}
                correctedResponse={item.result_detail}
                isCorrected={isCorrected}
              />
            )}
            
            {/* View Diff button for corrected items */}
            {isCorrected && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 h-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDiff(item);
                }}
              >
                <ArrowRightLeftIcon className="w-3.5 h-3.5" />
                Diff
              </Button>
            )}
            
            {/* Show dash if no actions available */}
            {!hasResponse && !isCorrected && (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
        );
      }
    }
  ], [onViewDiff]);

  return (
    <PaginatedTable
      data={items}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      serverSidePagination={true}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      rowClassName="hover:bg-accent/20 transition-colors"
    />
  );
};

export default ReviewItemsTable;
